import Configuration from "../../Configuration";

interface Dimensions {
    width: number;
    height: number;
}

export interface MediaFileProperties {
    fileProperties: {
        id: number;
        projectId: number;
        path: string;
        token: string;
    };
    mediaProperties: {
        type: string;
        size: number;
        duration: number;
        audioStreamIndex: number;
        audioStreamPid: number;
        audioCodec: string;
        audioBitrate: number;
        audioSamplerate: number;
        videoStreamIndex: number;
        videoStreamPid: number;
        videoCodec: string;
        videoWidth: number;
        videoHeight: number;
        videoAspectRatio: string;
        videoBitrate: number;
        videoFramerateString: string;
        videoFramerateDouble: number;
        videoProfile: string;
        videoLevel: number;
        videoRefFrames: number;
        videoColorPrimaries: string;
        videoPixelFormat: string | "yuv420p";
    };
}

export interface EditOp {
    cropped: boolean;
    introkey: string;
    isduplicate: boolean;
    iscompilation: boolean;
    outrokey: string;
    overlaykey: string;
    overlaytext: string;
    padded: boolean;
    sourceid: number;
    trimend: number;
    trimmed: boolean;
    trimstart: number;
}

export interface PublishOp {
    date: string;
    formatkey: string;
    formatname: string;
    operation: string;
    publisherkey: string;
    publishername: string;
    publishertype: string;
    publishsettingkey: string;
    publishsettingname: string;
}

export interface CreateOp {
    compilationsourceids: number[];
    fromfile: boolean;
    fromselections: boolean;
    introkey: string;
    iscompilation: boolean;
    outrokey: string;
    selectioninfos: { broadcastdate: string; duration: number }[];
}
export interface FragmentProperties {
    key: "spoton-editop" | "spoton-createop" | "spoton-publishops" | string;
    value: EditOp | PublishOp[] | CreateOp | string | boolean;
}

interface FragmentVariant {
    formatKey: string | "mp4";
    formatName: string;
    mediaFileProperties: MediaFileProperties;
    language?: string;
    tags: string[];
}

export interface FragmentEntity {
    id: number;
    title: string;
    description: string;
    projectId: number;
    channelId: number;
    taskId: number;
    mainInGroup: boolean;
    groupId: number;
    userProperties: {
        id: number;
        username: string;
    };
    channelProperties: {
        id: number;
        key: string;
        name: string;
    };
    processing: boolean;
    processingTaskId: number;
    variants: FragmentVariant[];
    metaVariants: FragmentVariant[];
    thumbnail: MediaFileProperties;
    insertionDate: string;
    modifiedDate: string;
    tags: string[];
    properties: FragmentProperties[];
    broadcastDate: string;
}

export class Fragment {
    id: number;

    title: string;

    description: string;

    projectID: number;

    channelID: number;

    taskID: number;

    mainInGroup: boolean;

    groupID: number;

    userProperties: {
        id: number;
        username: string;
    };

    channelProperties: {
        id: number;
        key: string;
        name: string;
    };

    isProcessing: boolean;

    processingTaskId: number;

    variants: FragmentVariant[];

    metaVariants: FragmentVariant[];

    thumbnail: MediaFileProperties;

    creationDate: string;

    lastModifiedDate: string;

    tags: string[];

    properties: FragmentProperties[];

    broadcastDate: string;

    constructor(entity: FragmentEntity) {
        this.id = entity.id;
        this.title = entity.title;
        this.description = entity.description;
        this.projectID = entity.projectId;
        this.channelID = entity.channelId;
        this.taskID = entity.taskId;
        this.mainInGroup = entity.mainInGroup;
        this.groupID = entity.groupId;
        this.userProperties = entity.userProperties;
        this.channelProperties = entity.channelProperties;
        this.isProcessing = entity.processing;
        this.processingTaskId = entity.processingTaskId;
        this.variants = entity.variants;
        this.metaVariants = entity.metaVariants;
        this.thumbnail = entity.thumbnail;
        this.creationDate = entity.insertionDate;
        this.lastModifiedDate = entity.modifiedDate;
        this.tags = entity.tags;
        this.properties = entity.properties;
        this.broadcastDate = entity.broadcastDate;
    }

    static fromVariant(fragment: Fragment, variant: FragmentVariant): Fragment {
        const entity: FragmentEntity = {
            id: fragment.id,
            title: fragment.title,
            description: fragment.description,
            projectId: fragment.projectID,
            channelId: fragment.channelID,
            taskId: fragment.taskID,
            mainInGroup: fragment.mainInGroup,
            groupId: fragment.groupID,
            userProperties: fragment.userProperties,
            channelProperties: fragment.channelProperties,
            processing: fragment.isProcessing,
            processingTaskId: fragment.processingTaskId,
            variants: [variant],
            metaVariants: [variant],
            thumbnail: fragment.thumbnail,
            insertionDate: fragment.creationDate,
            modifiedDate: fragment.lastModifiedDate,
            tags: fragment.tags,
            properties: fragment.properties,
            broadcastDate: fragment.broadcastDate,
        };
        return new Fragment(entity);
    }

    getUniqueIdentifier(): string {
        const variantID =
            this.variants.length > 0
                ? this.variants[0].mediaFileProperties.fileProperties.id
                : "";
        return `${this.id}_${variantID}`;
    }

    // Processing can also mean that we are simply generating
    // another variant of the same fragment
    isAvailable(): boolean {
        return this.variants.length > 0;
    }

    getThumbnailURL = (): string => {
        const FILE_API_URL = Configuration.get("fileApiURL");
        if (!this.thumbnail) {
            return "";
        }
        return `${FILE_API_URL}/api/file/read/v1/t/p/${this.thumbnail.fileProperties.token}/${this.thumbnail.fileProperties.path}`;
    };

    getMediaType = (): string => {
        if (this.variants.length === 0) {
            return "";
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.type;
    };

    getMediaDimensions = (): Dimensions => {
        if (this.variants.length === 0) {
            return { width: 0, height: 0 };
        }
        const variant = this.variants[0];
        return {
            width: variant.mediaFileProperties.mediaProperties.videoWidth,
            height: variant.mediaFileProperties.mediaProperties.videoHeight,
        };
    };

    getAspectRatio = (): string => {
        if (this.variants.length === 0) {
            return "";
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.videoAspectRatio;
    };

    getFrameRate = (): string => {
        if (this.variants.length === 0) {
            return "0";
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.videoFramerateString;
    };

    getFrameRateDouble = (): number => {
        if (this.variants.length === 0) {
            return 0;
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.videoFramerateDouble;
    };

    getVideoBitrate = (): number => {
        if (this.variants.length === 0) {
            return 0;
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.videoBitrate;
    };

    getAudioBitrate = (): number => {
        if (this.variants.length === 0) {
            return 0;
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.audioBitrate;
    };

    getDuration = (): number => {
        if (this.variants.length === 0) {
            return 0;
        }
        const variant = this.variants[0];
        return variant.mediaFileProperties.mediaProperties.duration;
    };

    getMediaURL = (selectedVariant = 0): string => {
        const FILE_API_URL = Configuration.get("fileApiURL");
        if (this.variants.length === 0) {
            return "";
        }
        const variant = this.variants[selectedVariant];
        return `${FILE_API_URL}/api/file/read/v1/t/p/${variant.mediaFileProperties.fileProperties.token}/${variant.mediaFileProperties.fileProperties.path}`;
    };

    getMetaVariantURL = (selectedVariant = 0): string => {
        if (this.metaVariants.length === 0) {
            return "";
        }
        const FILE_API_URL = Configuration.get("fileApiURL");
        const variant = this.metaVariants[selectedVariant];
        return `${FILE_API_URL}/api/file/read/v1/t/p/${variant.mediaFileProperties.fileProperties.token}/${variant.mediaFileProperties.fileProperties.path}`;
    };

    getDownloadURL = (selectedVariant = 0): string => {
        const DOWNLOAD_API_URL = Configuration.get("siteApiURL");
        if (this.variants.length === 0) {
            return "";
        }
        const variant = this.variants[selectedVariant];
        const pathSplit =
            variant.mediaFileProperties.fileProperties.path.split(".");
        const extension = pathSplit.pop() || "";
        const friendlyTitle = this.title.replace(/[^\w\s]/gi, "_");
        return `${DOWNLOAD_API_URL}/api/fragment/read/v1/t/${variant.mediaFileProperties.fileProperties.token}/${this.id}/${variant.formatKey}/${friendlyTitle}.${extension}`;
    };

    hasSubtitles = (): boolean => this.metaVariants.some(
            (variant) => variant.formatKey === "subtitles_webvtt",
        );

    getSubtitlesAsString = async (): Promise<string | undefined> => {
        const subtitleVariantIdx = this.metaVariants.findIndex(
            (variant) => variant.formatKey === "subtitles_webvtt",
        );
        if (subtitleVariantIdx === -1) {
            return undefined;
        }
        return fetch(this.getMetaVariantURL(subtitleVariantIdx))
            .then((response) => response.text())
            .then((data) => data);
    };

    getSubtitlesLanguage = (): string | undefined => {
        if (this.metaVariants.length === 0) {
            return undefined;
        }
        const subtitleVariantIdx = this.metaVariants.findIndex(
            (variant) => variant.formatKey === "subtitles_webvtt",
        );
        if (subtitleVariantIdx === -1) {
            return undefined;
        }
        return this.metaVariants[subtitleVariantIdx].language;
    };
}
